import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';

/**
 * The calendar/clock components a format string is rendered from, independent of where they came from
 * (the live clock or a pinned FixedTime param).
 *
 * This IS the wall clock: csharp resolves to a bare DateTime whose Kind carries the local-vs-UTC choice,
 * so the choice is already applied by the time the formatter sees it. A JavaScript Date has no such tag —
 * its wall clock only materializes when a getter is chosen — so resolveCurrentTime applies the choice HERE
 * and hands on components, never a Date plus a flag. That keeps formatCurrentTime/buildRawTime at csharp's
 * arity instead of threading isLocalTime past the resolve step, and it is the same answer swift's
 * TimeFilter.DateParts gives for the same missing primitive.
 */
export interface DateParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

/**
 * The instant and format recovered from a raw time island.
 */
export interface ParsedRawTime {
    instant: DateParts;
    dateFormat: string;
}

/**
 * Purpose:
 * The time capability's SHARED, PURE param-read/resolve/format logic — the single source of truth for the
 * rendered CurrentTime, consumed identically by every tier.
 *
 * Responsibilities:
 * - Read the DateFormat / IsLocalTime / FixedTime params off a binding's param JSON
 * - Resolve the instant to render (pinned FixedTime when declared, otherwise the live clock)
 * - Format that instant, and build/parse the raw island the client tier round-trips
 *
 * Design:
 * This type is deliberately DEPENDENCY-FREE: no file IO, no ConfigFile, no registry, no handler. It exists
 * so that no handler has to depend on another handler to reuse this logic — TimeData (server data
 * provider), TimeClientData (server raw preparer) and TimeClient (client processor) each depend ONLY on
 * this type, never on each other. Because it is pure it bundles into the browser untouched, which is what
 * lets the client processor format the byte-identical string in the browser, in --apiclient and in
 * --ociclient (the single-source guarantee, §3.7).
 */
export class TimeFilter {
    /**
     * The format applied when a binding declares no DateFormat.
     */
    static readonly defaultDateFormat: string = 'DD-MMM-YYYY hh:mm:ss';

    /**
     * The result key applied when a binding declares no TimeKey.
     */
    static readonly defaultTimeKey: string = 'CurrentTime';

    /**
     * The one spelling a FixedTime param may use, shared by every language's time tier and by the
     * --actiontests harness. Deliberately culture-invariant and offset-free: the instant is a literal wall
     * clock, so no language applies a timezone conversion to it and every language formats the same bytes
     * regardless of the host's zone. That is what makes the cross-language compare exact.
     */
    static readonly fixedTimeWireFormat: string = 'yyyy-MM-ddTHH:mm:ss';

    /**
     * Reads the DateFormat param, falling back to defaultDateFormat when absent or empty.
     */
    static readDateFormat(paramJson: JsonObject | null): string {
        if (paramJson === null || paramJson === undefined || TimeFilter.hasKey(paramJson, 'DateFormat') === false) {
            return TimeFilter.defaultDateFormat;
        }

        const dateFormatValue = TimeFilter._localGetValue(paramJson, 'DateFormat');
        if (dateFormatValue === null || dateFormatValue === undefined || dateFormatValue.toString() === '') {
            return TimeFilter.defaultDateFormat;
        }

        return dateFormatValue.toString();
    }

    /**
     * Reads the IsLocalTime param, defaulting to true when absent. Accepts a real bool or its string
     * spelling, matching how the param files write it.
     */
    static readIsLocalTime(paramJson: JsonObject | null): boolean {
        if (paramJson === null || paramJson === undefined || TimeFilter.hasKey(paramJson, 'IsLocalTime') === false) {
            return true;
        }

        const isLocalTimeValue = TimeFilter._localGetValue(paramJson, 'IsLocalTime');
        if (typeof isLocalTimeValue === 'boolean') {
            return isLocalTimeValue;
        }
        if (isLocalTimeValue !== null && isLocalTimeValue !== undefined) {
            return isLocalTimeValue.toString().toLowerCase() === 'true';
        }

        return true;
    }

    /**
     * Reads the optional FixedTime param. An empty result means "use the live clock".
     */
    static readFixedTime(paramJson: JsonObject | null): string {
        if (paramJson === null || paramJson === undefined || TimeFilter.hasKey(paramJson, 'FixedTime') === false) {
            return '';
        }

        const fixedTimeValue = TimeFilter._localGetValue(paramJson, 'FixedTime');
        if (fixedTimeValue === null || fixedTimeValue === undefined || fixedTimeValue.toString() === '') {
            return '';
        }

        return fixedTimeValue.toString();
    }

    /**
     * Resolves the instant to render: the pinned FixedTime when the binding declares one, otherwise the
     * live clock.
     *
     * A pinned instant is taken LITERALLY — IsLocalTime is deliberately ignored for it: the value was
     * assembled from literal components, so those components ARE the wall clock. Applying a zone
     * conversion would make the rendered bytes depend on the host's timezone, which is precisely the
     * non-determinism FixedTime exists to remove; two languages on machines in different zones must render
     * the same string. An unparseable FixedTime falls back to the live clock rather than throwing, so a
     * typo degrades to today's behaviour instead of taking the AppSite down — and --actiontests catches
     * it, because its exact-match branch will not match a live clock.
     */
    static resolveCurrentTime(fixedTime: string, isLocalTime: boolean): DateParts {
        const pinned = TimeFilter.parseFixedTime(fixedTime);
        if (pinned !== null) {
            return pinned;
        }

        return TimeFilter.nowParts(isLocalTime);
    }

    /**
     * Decomposes the live clock into wall-clock components, applying the local-vs-UTC choice HERE so that
     * nothing downstream has to know which getter produced them — the point at which csharp's DateTime.Now
     * vs DateTime.UtcNow makes the same choice.
     */
    static nowParts(isLocalTime: boolean): DateParts {
        const now = new Date();
        return isLocalTime
            ? {
                year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate(),
                hour: now.getHours(), minute: now.getMinutes(), second: now.getSeconds(),
            }
            : {
                year: now.getUTCFullYear(), month: now.getUTCMonth() + 1, day: now.getUTCDate(),
                hour: now.getUTCHours(), minute: now.getUTCMinutes(), second: now.getUTCSeconds(),
            };
    }

    /**
     * Parses a FixedTime param in the one cross-language wire spelling, "yyyy-MM-ddTHH:mm:ss".
     *
     * Matched by regex and assembled component-wise rather than handed to Date.parse: the built-in parser
     * accepts many spellings and its timezone treatment of an offset-free string has varied across engines,
     * either of which would let two languages render different bytes for the same param. An unparseable
     * value returns null so the caller falls back to the live clock, degrading to today's behaviour instead
     * of taking the AppSite down — --actiontests catches it, since an exact-match will not match a clock.
     */
    static parseFixedTime(fixedTime: string): DateParts | null {
        if (fixedTime === '') {
            return null;
        }
        const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/.exec(fixedTime);
        if (m === null) {
            return null;
        }
        const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
        const hour = Number(m[4]), minute = Number(m[5]), second = Number(m[6]);
        // Reject values that look well-formed but are not real dates (e.g. month 13, 31 February). A Date is
        // built ONLY to run that calendar check — the components it round-trips are what get returned, so no
        // zone is ever applied to a pinned value.
        const probe = new Date(year, month - 1, day, hour, minute, second);
        if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day
            || probe.getHours() !== hour || probe.getMinutes() !== minute || probe.getSeconds() !== second) {
            return null;
        }
        return { year: year, month: month, day: day, hour: hour, minute: minute, second: second };
    }

    /**
     * Formats a resolved instant under the binding's DateFormat. Shared by the server GetData and the
     * client processor so both tiers emit the same bytes. Supports C# DateTime format patterns.
     */
    static formatCurrentTime(instant: DateParts, dateFormat: string): string {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const day = String(instant.day).padStart(2, '0');
        const monthNum = String(instant.month).padStart(2, '0');
        const yearFull = instant.year.toString();
        const hour12 = String(instant.hour % 12 || 12).padStart(2, '0');
        const hour24 = String(instant.hour).padStart(2, '0');
        const minute = String(instant.minute).padStart(2, '0');
        const second = String(instant.second).padStart(2, '0');
        const yearShort = yearFull.slice(-2);
        const monthAbbr = months[instant.month - 1];

        // Replace format patterns in order of specificity (longer patterns first)
        let result = dateFormat;
        result = result.replaceAll('yyyy', yearFull);       // 4-digit year
        result = result.replaceAll('MMM', monthAbbr);       // 3-letter month abbreviation
        result = result.replaceAll('MM', monthNum);         // 2-digit month
        result = result.replaceAll('dd', day);              // 2-digit day
        result = result.replaceAll('HH', hour24);           // 24-hour format
        result = result.replaceAll('hh', hour12);           // 12-hour format
        result = result.replaceAll('mm', minute);           // Minutes
        result = result.replaceAll('ss', second);           // Seconds
        result = result.replaceAll('yy', yearShort);        // 2-digit year (after yyyy to avoid conflict)

        return result;
    }

    /**
     * Builds the RAW time island shipped to the browser: the already-resolved instant in the invariant
     * wire format plus the DateFormat to apply. The instant is resolved SERVER-side on purpose — the
     * browser clock is not the render clock, and a client-resolved instant would drift from the server
     * render and from every other language. The DateFormat travels with it because the browser has no
     * access to the server param files (the same reason ScenarioClientData bakes its static skips in).
     *
     * The wire string is the WALL CLOCK the server already resolved, so no zone choice is made or shipped
     * here: the client reads the components back literally.
     */
    static buildRawTime(instant: DateParts, dateFormat: string): JsonObject {
        const rawDataJson = new JsonObject();
        rawDataJson.set('Instant', TimeFilter.formatCurrentTime(instant, TimeFilter.fixedTimeWireFormat));
        rawDataJson.set('DateFormat', dateFormat);
        return rawDataJson;
    }

    /**
     * Parses a raw time island back into its instant and DateFormat. Returns null when the island is
     * absent or malformed, so the client processor can yield an empty result rather than render a
     * fabricated time.
     */
    static parseRawTime(rawJson: JsonObject | null): ParsedRawTime | null {
        if (rawJson === null || rawJson === undefined || rawJson.containsKey('Instant') === false) {
            return null;
        }

        let dateFormat = TimeFilter.defaultDateFormat;
        if (rawJson.containsKey('DateFormat') === true && rawJson.get('DateFormat').isString === true) {
            const parsedFormat = rawJson.get('DateFormat').getString();
            if (parsedFormat !== null && parsedFormat !== '') {
                dateFormat = parsedFormat;
            }
        }

        const instantValue = rawJson.get('Instant').toString();
        if (instantValue === null || instantValue === '') {
            return null;
        }

        const instant = TimeFilter.parseFixedTime(instantValue);
        if (instant === null) {
            return null;
        }

        return { instant: instant, dateFormat: dateFormat };
    }

    /**
     * Reads the TimeKey param, falling back to defaultTimeKey when absent or blank. Parameterising the
     * projected key is what lets ONE handler serve both the CurrentTime and the ServerTime bindings of the
     * 85-series — the server binds one TimeData twice by IParamFile, and the client tier binds one
     * TimeClient twice, rather than either needing a near-duplicate class per key.
     */
    static readTimeKey(paramJson: JsonObject | null): string {
        if (paramJson === null || paramJson === undefined || TimeFilter.hasKey(paramJson, 'TimeKey') === false) {
            return TimeFilter.defaultTimeKey;
        }

        const timeKeyValue = paramJson.getString('TimeKey');
        if (timeKeyValue === null || timeKeyValue === undefined || timeKeyValue.trim() === '') {
            return TimeFilter.defaultTimeKey;
        }

        return timeKeyValue;
    }

    /**
     * Builds the time capability's result JsonObject under the declared key. Shared by the server GetData
     * and the client processor so the projected key is identical across tiers.
     */
    static buildTimeResult(timeKey: string, currentTime: string): JsonObject {
        const retDataJson = new JsonObject();
        retDataJson.set(timeKey, currentTime);
        return retDataJson;
    }

    /**
     * Checks if an object has a key - supports Map, wrappers with has/containsKey, and plain objects.
     *
     * Public because the time tier probes componentJson for CurrentTime as well as paramJson for its own
     * params, and both reach this handler through the same loosely-typed merge path.
     */
    static hasKey(obj: JsonObject, key: string): boolean {
        if (obj instanceof Map) {
            return obj.has(key);
        }
        if (typeof (obj as { has?: (k: string) => boolean }).has === 'function') {
            return (obj as { has: (k: string) => boolean }).has(key);
        }
        if (typeof (obj as { containsKey?: (k: string) => boolean }).containsKey === 'function') {
            return (obj as { containsKey: (k: string) => boolean }).containsKey(key);
        }
        return key in obj;
    }

    /**
     * Gets a value from an object - supports Map, wrappers with get method, and plain objects.
     */
    private static _localGetValue(obj: JsonObject, key: string): JsonValue | undefined {
        if (obj instanceof Map) {
            return obj.get(key);
        }
        if (typeof (obj as { get?: (k: string) => JsonValue }).get === 'function') {
            return (obj as { get: (k: string) => JsonValue }).get(key);
        }
        return (obj as unknown as Record<string, JsonValue>)[key];
    }
}
